/**
 * Catering catalogue mappers — the single translation layer between the
 * backend `events/api/v1` shapes (Menu Items, Courses, Packages, Package
 * Categories, Add-ons) and the UI types this feature renders.
 *
 * Pure functions only (no `server-only`, no React, no fetch) so the same code
 * runs in the SSR fetchers, the server actions, and the client slice/hooks.
 */
import type {
  AddOnItem,
  Certification,
  Course,
  Ingredient,
  MenuGroup,
  MenuItem,
  PackageItem,
  PackagePerformance,
  PackageStructureCourse,
  PackageTagColor,
  Venue,
} from "./types";

// ─── Backend (read) shapes ────────────────────────────────────────────────

export interface ApiCourse {
  id: string;
  name: string;
  sort_order: number;
  is_seed?: boolean;
  deleted_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ApiIngredient {
  name: string;
  quantity: number;
  unit: string;
}

/** Dietary certification enum as the backend stores it. */
export type ApiCertification = "halal" | "vegan" | "nut_free" | "gluten_free";

export interface ApiMenuItem {
  id: string;
  name: string;
  description: string | null;
  course: { id: string; name: string; sort_order?: number } | null;
  price: string | null;
  price_unit: string | null;
  minimum_price: string | null;
  calories: number | null;
  ingredients: ApiIngredient[] | null;
  certifications: ApiCertification[] | null;
  photo: string | null;
  status: "draft" | "published" | "archived";
  is_active: boolean;
  deleted_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

/** Page-number paginated envelope returned by the list endpoints. */
export interface ApiPage<T> {
  count: number;
  next: string | null;
  previous: string | null;
  total_pages?: number;
  current_page?: number;
  results: T[];
}

/** Accept either a paginated page or a bare array and return the rows. */
export function unwrapResults<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === "object" && "results" in payload) {
    const r = (payload as { results?: unknown }).results;
    return Array.isArray(r) ? (r as T[]) : [];
  }
  return [];
}

// Re-exported from the shared util so catering + venues share one guard.
export { isDisplayablePhoto } from "@/utils/isDisplayablePhoto";

// ─── Enum maps ────────────────────────────────────────────────────────────

const CERT_API_TO_UI: Record<ApiCertification, Certification> = {
  halal: "halal",
  vegan: "vegan",
  nut_free: "nut",
  gluten_free: "gluten-free",
};

const CERT_UI_TO_API: Record<Certification, ApiCertification> = {
  halal: "halal",
  vegan: "vegan",
  nut: "nut_free",
  "gluten-free": "gluten_free",
};

export const uiCertToApi = (c: Certification): ApiCertification =>
  CERT_UI_TO_API[c];
export const apiCertToUi = (c: ApiCertification): Certification | null =>
  CERT_API_TO_UI[c] ?? null;

/**
 * `price_unit` is a fixed backend enum (not a list endpoint), so the dropdown
 * options carry the raw enum value and a human label. Source of truth here.
 */
export const PRICE_UNIT_API_OPTIONS: { label: string; value: string }[] = [
  { label: "Per person", value: "per_person" },
  { label: "Per plate", value: "per_plate" },
  { label: "Per unit", value: "per_unit" },
  // BE-confirmed flat value is "flat" (not "flat_fee").
  { label: "Flat fee", value: "flat" },
];

export const priceUnitLabel = (value: string | null | undefined): string =>
  PRICE_UNIT_API_OPTIONS.find((o) => o.value === value)?.label ?? "Per person";

// ─── Gradient palette ─────────────────────────────────────────────────────
// The cards are colour-coded by a gradient. The backend has no such field, so
// pick a stable gradient per id (deterministic hash) to keep the look intact.

const GRADIENTS: { from: string; to: string }[] = [
  { from: "from-rose-100", to: "to-rose-300" },
  { from: "from-amber-100", to: "to-amber-300" },
  { from: "from-green-100", to: "to-green-300" },
  { from: "from-orange-100", to: "to-orange-300" },
  { from: "from-lime-100", to: "to-lime-300" },
  { from: "from-red-100", to: "to-red-300" },
  { from: "from-sky-100", to: "to-sky-300" },
  { from: "from-purple-100", to: "to-purple-300" },
];

export function gradientForId(id: string): { from: string; to: string } {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return GRADIENTS[Math.abs(hash) % GRADIENTS.length];
}

// ─── Course mapping ───────────────────────────────────────────────────────

export const mapApiCourse = (c: ApiCourse): Course => ({
  id: c.id,
  name: c.name,
  sortOrder: c.sort_order ?? 0,
});

export const courseToOption = (
  c: Course,
): { label: string; value: string } => ({
  label: c.name,
  value: c.id,
});

// ─── Menu item mapping ────────────────────────────────────────────────────

export function mapApiMenuItem(api: ApiMenuItem): MenuItem {
  const ingredientList: Ingredient[] = (api.ingredients ?? []).map((i) => ({
    name: i.name,
    amount: String(i.quantity ?? ""),
    unit: i.unit,
  }));
  const certifications: Certification[] = (api.certifications ?? [])
    .map(apiCertToUi)
    .filter((c): c is Certification => c !== null);
  const gradient = gradientForId(api.id);

  return {
    id: api.id,
    name: api.name ?? "Untitled",
    courseName: api.course?.name ?? "",
    courseId: api.course?.id,
    price: api.price != null ? Number(api.price) : 0,
    priceUnit: priceUnitLabel(api.price_unit),
    priceUnitValue: api.price_unit ?? "per_person",
    minimumPrice:
      api.minimum_price != null ? Number(api.minimum_price) : undefined,
    kcal: api.calories ?? 0,
    description: api.description ?? "",
    ingredients: ingredientList
      .map((i) => i.name)
      .filter(Boolean)
      .join(" · "),
    ingredientList,
    certifications,
    // UI "active" == backend "published"; everything else shows as a draft.
    status: api.status === "published" ? "active" : "draft",
    isActive: api.is_active,
    updatedAt: api.updated_at,
    photo: api.photo ?? null,
    gradientFrom: gradient.from,
    gradientTo: gradient.to,
  };
}

// ─── Menu write payload ───────────────────────────────────────────────────

/** Loose, UI-shaped input the menu form collects. Mapped to the API body. */
export interface MenuItemDraftPayload {
  name?: string;
  courseId?: string;
  description?: string;
  price?: string;
  priceUnit?: string;
  minimumPrice?: string;
  calories?: string;
  ingredients?: Ingredient[];
  certifications?: Certification[];
  isActive?: boolean;
}

/** Build the JSON body for create/update. Omits empty values so the
 *  save-as-you-go draft flow doesn't clobber fields the user left blank. */
export function buildMenuItemBody(
  input: MenuItemDraftPayload,
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (input.name !== undefined) body.name = input.name;
  if (input.courseId) body.course_id = input.courseId;
  if (input.description !== undefined) body.description = input.description;
  if (input.price !== undefined && input.price !== "")
    body.price = String(input.price);
  if (input.priceUnit) body.price_unit = input.priceUnit;
  if (input.minimumPrice !== undefined && input.minimumPrice !== "")
    body.minimum_price = String(input.minimumPrice);
  if (input.calories !== undefined && input.calories !== "")
    body.calories = Number(input.calories);
  if (input.ingredients) {
    body.ingredients = input.ingredients
      .filter((i) => i.name.trim())
      .map((i) => ({
        name: i.name.trim(),
        quantity: Number(i.amount) || 0,
        unit: i.unit || "gm",
      }));
  }
  if (input.certifications)
    body.certifications = input.certifications.map(uiCertToApi);
  if (input.isActive !== undefined) body.is_active = input.isActive;
  return body;
}

// ─── List grouping (UI consumption) ───────────────────────────────────────

/** Published items grouped by course, groups ordered by `courseOrder`. */
export function buildMenuGroups(
  items: MenuItem[],
  courseOrder: string[] = [],
): MenuGroup[] {
  const byCourse = new Map<string, MenuItem[]>();
  for (const item of items) {
    if (item.status !== "active") continue;
    const key = item.courseName || "Uncategorised";
    const bucket = byCourse.get(key);
    if (bucket) bucket.push(item);
    else byCourse.set(key, [item]);
  }
  const orderIndex = (name: string) => {
    const i = courseOrder.indexOf(name);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  return Array.from(byCourse.entries())
    .sort(([a], [b]) => orderIndex(a) - orderIndex(b) || a.localeCompare(b))
    .map(([course, groupItems]) => ({ course, items: groupItems }));
}

/** Draft rows for the Draft Menu drawer. */
export function pickMenuDrafts(
  items: MenuItem[],
): { id: string; name: string; courseName: string; price: number }[] {
  return items
    .filter((i) => i.status === "draft")
    .map((i) => ({
      id: i.id,
      name: i.name,
      courseName: i.courseName,
      price: i.price,
    }));
}

// ─── Package Category mapping ─────────────────────────────────────────────

/** Backend shape for a package category (List / Create / Update response). */
export interface ApiPackageCategory {
  id: string;
  name: string;
  sort_order: number;
  is_seed?: boolean;
  deleted_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

/** UI-shaped category (id + name + display order). */
export interface Category {
  id: string;
  name: string;
  sortOrder: number;
}

/** Map a backend category row to the UI Category shape. */
export const mapApiCategory = (c: ApiPackageCategory): Category => ({
  id: c.id,
  name: c.name,
  sortOrder: c.sort_order ?? 0,
});

/** Turn a Category into a `{ label, value }` option pair for CSelect. */
export const categoryToOption = (
  c: Category,
): { label: string; value: string } => ({
  label: c.name,
  value: c.id,
});

// ─── Venue mapping ────────────────────────────────────────────────────────

/** Backend venue row (operations `GET /venues/`); we only need id + name. */
export interface ApiVenue {
  id: string;
  name: string;
  active?: boolean;
}

/** Map a backend venue row to the lightweight UI `Venue` (id + name). */
export const mapApiVenue = (v: ApiVenue): Venue => ({ id: v.id, name: v.name });

// ─── Package API shapes ───────────────────────────────────────────────────

/**
 * One item row inside a package (from the detail response `items` array).
 * The backend returns a join record with a nested `menu_item`.
 */
export interface ApiPackageItem {
  id: string;
  menu_item: {
    id: string;
    name: string;
    price: string | null;
    course: { id: string; name: string } | null;
  };
  quantity: number;
  sort_order: number;
}

/** Package SUMMARY row (list endpoint). */
export interface ApiPackageSummary {
  id: string;
  name: string;
  category: { id: string; name: string } | null;
  base_price: string;
  pricing_type: string;
  minimum_price: string | null;
  photo: string | null;
  available_at_all_venues: boolean;
  status: "draft" | "published" | "archived";
  is_active: boolean;
  courses_count: number;
  menu_items_count: number;
  times_booked: number;
  total_revenue: string;
  created_at: string;
  updated_at: string;
}

/** Package DETAIL row (single-package endpoint) — extends SUMMARY. */
export interface ApiPackageDetail extends ApiPackageSummary {
  description: string | null;
  items: ApiPackageItem[];
  venues: Array<{ id: string; venue: { id: string; name: string } }>;
  base_price_reference: string;
  created_by: { id: string; name: string } | null;
  deleted_at?: string | null;
}

/** Performance metrics from `GET /packages/{id}/performance/`. */
export interface ApiPackagePerformance {
  times_booked: number;
  total_revenue: string | null;
  total_guests: number;
  avg_booking_value: string | null;
  avg_guests_value: string | null;
  last_updated_at: string | null;
}

// ─── Package tag-color palette ────────────────────────────────────────────
// Deterministic hash of the category name → one of the UI badge colours.

const PKG_TAG_COLORS: PackageTagColor[] = [
  "primary",
  "warning",
  "success",
  "purple",
  "info",
  "teal",
  "neutral",
  "gray",
];

/** Pick a stable `PackageTagColor` from the category name (or id as fallback). */
function tagColorForName(name: string): PackageTagColor {
  let hash = 0;
  for (let i = 0; i < name.length; i++)
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return PKG_TAG_COLORS[Math.abs(hash) % PKG_TAG_COLORS.length];
}

// ─── Package mapping (SUMMARY → PackageItem) ─────────────────────────────

/**
 * Map a backend package summary row to the UI `PackageItem` shape.
 * pricing_type values from the API: "per_person" | "flat_fee"
 * NOTE: The exact enum values ("per_person" / "flat_fee") are best-guess
 * from the Postman collection. Pending BE confirmation doc.
 */
export function mapApiPackageSummary(api: ApiPackageSummary): PackageItem {
  const gradient = gradientForId(api.id);
  const categoryName = api.category?.name ?? "Regular";
  return {
    id: api.id,
    name: api.name,
    tier: api.name,
    pricePerPerson: Number(api.base_price) || 0,
    courses: api.courses_count,
    items: api.menu_items_count,
    typeBadge: {
      label: categoryName,
      color: tagColorForName(categoryName),
    },
    status: api.status === "published" ? "active" : "draft",
    isActive: api.is_active,
    photo: api.photo ?? null,
    gradientFrom: gradient.from,
    gradientTo: gradient.to,
    pricingType:
      api.pricing_type === "per_person"
        ? "Per person"
        : api.pricing_type === "flat"
          ? "Flat fee"
          : api.pricing_type,
    // Keep the category id so the edit form can preselect the right option.
    categoryId: api.category?.id,
    availableAtAllVenues: api.available_at_all_venues,
    updatedAt: api.updated_at,
  };
}

/**
 * Build the `menuStructure` from the detail `items` array, grouping by course
 * name to produce `PackageStructureCourse[]`.
 */
export function buildPackageMenuStructure(
  items: ApiPackageItem[],
): PackageStructureCourse[] {
  const byCourse = new Map<string, PackageStructureCourse>();
  for (const row of items) {
    const courseName = row.menu_item.course?.name ?? "Uncategorised";
    let group = byCourse.get(courseName);
    if (!group) {
      group = { course: courseName, items: [] };
      byCourse.set(courseName, group);
    }
    group.items.push({
      id: row.menu_item.id,
      name: row.menu_item.name,
      price: Number(row.menu_item.price) || 0,
    });
  }
  return Array.from(byCourse.values());
}

/**
 * Map a backend performance object to `PackagePerformance`.
 * avg_guests_value is labelled "avg_guests_value" in the API but represents
 * average guests per booking.
 */
export function mapApiPerformance(
  api: ApiPackagePerformance,
): PackagePerformance {
  return {
    timesBooked: api.times_booked,
    totalRevenue: Number(api.total_revenue ?? 0),
    avgBookingValue: Number(api.avg_booking_value ?? 0),
    avgGuestCount: api.total_guests,
  };
}

/**
 * Map a package detail (with items + perf already fetched) to a full
 * `PackageItem` ready for the details panel and the edit form.
 */
export function mapApiPackageDetail(
  detail: ApiPackageDetail,
  perf?: ApiPackagePerformance,
): PackageItem {
  const base = mapApiPackageSummary(detail);
  return {
    ...base,
    description: detail.description ?? undefined,
    menuStructure: buildPackageMenuStructure(detail.items),
    performance: perf ? mapApiPerformance(perf) : undefined,
    // Scoped venue ids (from the nested venue join) for edit-mode prefill.
    venueIds: detail.venues.map((v) => v.venue.id),
  };
}

// ─── Package write payload ────────────────────────────────────────────────

/** Loose UI form fields collected by the package form wizard. */
export interface PackageDraftPayload {
  name?: string;
  /** Category id (UUID) selected from the package-categories API. */
  categoryId?: string;
  description?: string;
  /** Pricing type — UI slug ("per-person" / "flat-fee"), mapped to the backend
   *  enum ("per_person" / "flat") in buildPackageBody. */
  pricingType?: string;
  /** Price string for per-person pricing. */
  basePricePerson?: string;
  /** Price string for flat-fee pricing. */
  flatBaseFee?: string;
  /** True = offered at every venue; false = scoped to `venueIds`. */
  availableAtAllVenues?: boolean;
  /** Specific venue ids when not available at all venues. */
  venueIds?: string[];
  isActive?: boolean;
}

/**
 * Build the JSON body for POST /packages/ or PATCH /packages/{id}/.
 * pricing_type enum is BE-confirmed: "per_person" | "flat" (NOT "flat_fee").
 * The Pricing step sends only `base_price` + `pricing_type` (+ venue scope) —
 * there is no minimum-spend concept, so `minimum_price` is never sent (A1.3).
 */
export function buildPackageBody(
  input: PackageDraftPayload,
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (input.name !== undefined) body.name = input.name;
  // category_id: send only if a real UUID is provided (not empty/placeholder)
  if (input.categoryId) body.category_id = input.categoryId;
  if (input.description !== undefined) body.description = input.description;

  // Map UI pricing type ("per-person" | "flat-fee") to the backend enum.
  // BE-confirmed flat value is "flat" (sending "flat_fee" is rejected 400).
  const apiPricingType =
    input.pricingType === "per-person"
      ? "per_person"
      : input.pricingType === "flat-fee"
        ? "flat"
        : input.pricingType;

  if (apiPricingType) body.pricing_type = apiPricingType;

  // base_price is the single price field for both per-person and flat modes.
  const basePrice =
    input.pricingType === "flat-fee"
      ? input.flatBaseFee
      : input.basePricePerson;
  if (basePrice !== undefined && basePrice !== "")
    body.base_price = String(basePrice);

  // NOTE: there is no minimum-spend field — the BE declined adding one and
  // `minimum_price` stays BE-internal (answer/2026-06-15-catering-ui-driven-
  // additions-answers.md). The "Min Spend Guarantee" input was dropped.

  // Venue scope: all venues unless the form selected a specific venue.
  body.available_at_all_venues = input.availableAtAllVenues ?? true;

  if (input.isActive !== undefined) body.is_active = input.isActive;
  return body;
}

/** Draft package rows for the Draft Package drawer. */
export function pickPackageDrafts(
  packages: PackageItem[],
): { id: string; name: string; courseName: string; price: number }[] {
  return packages
    .filter((p) => p.status === "draft")
    .map((p) => ({
      id: p.id,
      name: p.name,
      courseName: "",
      price: p.pricePerPerson,
    }));
}

// ─── Add-on API shapes ────────────────────────────────────────────────────

/** Backend shape for an add-on row (list / create / update / detail). */
export interface ApiAddon {
  id: string;
  name: string;
  description: string | null;
  price: string;
  photo: string | null;
  status: "draft" | "published" | "archived";
  is_active: boolean;
  deleted_at?: string | null;
  created_at?: string;
  updated_at?: string;
  created_by?: { id: string; name: string } | null;
}

/** Map a backend addon row to the UI `AddOnItem` shape. */
export function mapApiAddon(api: ApiAddon): AddOnItem {
  const gradient = gradientForId(api.id);
  return {
    id: api.id,
    name: api.name,
    description: api.description ?? "",
    price: Number(api.price) || 0,
    status: api.status === "published" ? "active" : "draft",
    isActive: api.is_active,
    photo: api.photo ?? null,
    gradientFrom: gradient.from,
    gradientTo: gradient.to,
    updatedAt: api.updated_at,
  };
}

/** Add-on write payload from the form. */
export interface AddonDraftPayload {
  name?: string;
  description?: string;
  price?: string;
  isActive?: boolean;
}

/** Build the JSON body for POST /addons/ or PATCH /addons/{id}/. */
export function buildAddonBody(
  input: AddonDraftPayload,
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (input.name !== undefined) body.name = input.name;
  if (input.description !== undefined) body.description = input.description;
  if (input.price !== undefined && input.price !== "")
    body.price = String(input.price);
  if (input.isActive !== undefined) body.is_active = input.isActive;
  return body;
}

/** Draft add-on rows for the Draft Add-ons drawer. */
export function pickAddonDrafts(
  addOns: AddOnItem[],
): { id: string; name: string; price: number }[] {
  return addOns
    .filter((a) => a.status === "draft")
    .map((a) => ({ id: a.id, name: a.name, price: a.price }));
}
